"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { ArrowLeft } from "lucide-react";

export default function ComingSoon() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  // Create particles on mount
  useEffect(() => {
    const container = document.getElementById('cs-particles');
    if (!container) return;
    
    for (let i = 0; i < 20; i++) {
      const particle = document.createElement('div');
      particle.className = 'cs-particle';
      particle.style.left = Math.random() * 100 + '%';
      particle.style.animationDuration = (Math.random() * 15 + 10) + 's';
      particle.style.animationDelay = Math.random() * 8 + 's';
      const size = (Math.random() * 4 + 2) + 'px';
      particle.style.width = size;
      particle.style.height = size;
      container.appendChild(particle);
    }

    return () => {
      if (container) container.innerHTML = '';
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("loading");

    try {
      const res = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source: "login-page" }),
      });

      const data = await res.json();

      if (res.ok) {
        setStatus("success");
        setMessage(data.message || "You're on the list!");
        setEmail("");
      } else {
        setStatus("error");
        setMessage(data.error || "Something went wrong");
      }
    } catch {
      setStatus("error");
      setMessage("Something went wrong. Please try again.");
    }
  };

  const features = [
    { 
      gradient: "from-violet-500 via-purple-500 to-fuchsia-500",
      title: "AI Visual Studio", 
      desc: "Stunning graphics in seconds",
      icon: (
        <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="url(#grad1)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <defs>
            <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#a855f7" />
              <stop offset="100%" stopColor="#ec4899" />
            </linearGradient>
          </defs>
          <path d="M12 19l7-7 3 3-7 7-3-3z" />
          <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" />
          <path d="M2 2l7.586 7.586" />
          <circle cx="11" cy="11" r="2" />
        </svg>
      )
    },
    { 
      gradient: "from-cyan-500 via-blue-500 to-indigo-500",
      title: "Video Creation", 
      desc: "Pro videos, no editing skills",
      icon: (
        <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="url(#grad2)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <defs>
            <linearGradient id="grad2" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#06b6d4" />
              <stop offset="100%" stopColor="#6366f1" />
            </linearGradient>
          </defs>
          <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18" />
          <line x1="7" y1="2" x2="7" y2="22" />
          <line x1="17" y1="2" x2="17" y2="22" />
          <line x1="2" y1="12" x2="22" y2="12" />
          <line x1="2" y1="7" x2="7" y2="7" />
          <line x1="2" y1="17" x2="7" y2="17" />
          <line x1="17" y1="17" x2="22" y2="17" />
          <line x1="17" y1="7" x2="22" y2="7" />
        </svg>
      )
    },
    { 
      gradient: "from-orange-500 via-amber-500 to-yellow-500",
      title: "Full Autopilot", 
      desc: "AI runs your marketing",
      icon: (
        <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="url(#grad3)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <defs>
            <linearGradient id="grad3" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#f97316" />
              <stop offset="100%" stopColor="#eab308" />
            </linearGradient>
          </defs>
          <path d="M12 8V4H8" />
          <rect x="2" y="2" width="20" height="8" rx="2" />
          <rect x="2" y="14" width="20" height="8" rx="2" />
          <path d="M6 18h.01" />
          <path d="M10 18h.01" />
          <path d="M6 6h.01" />
          <path d="M10 6h.01" />
          <path d="M18 6h.01" />
        </svg>
      )
    },
  ];

  return (
    <>
      {/* Global Styles */}
      <style jsx global>{`
        .coming-soon-bg {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 0;
          overflow: hidden;
          background: linear-gradient(135deg, #0a0a0f 0%, #1a0a2e 40%, #0f0a1a 70%, #0a0a0f 100%);
        }

        /* Aurora Blobs */
        .cs-aurora-blob {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          will-change: transform, opacity;
        }

        .cs-blob-1 {
          top: -15%;
          left: 20%;
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, rgba(168, 85, 247, 0.4) 0%, transparent 70%);
          animation: cs-float-1 12s ease-in-out infinite;
        }

        .cs-blob-2 {
          bottom: -20%;
          right: 10%;
          width: 600px;
          height: 600px;
          background: radial-gradient(circle, rgba(236, 72, 153, 0.3) 0%, transparent 70%);
          animation: cs-float-2 15s ease-in-out infinite;
        }

        .cs-blob-3 {
          top: 40%;
          right: -10%;
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(59, 130, 246, 0.25) 0%, transparent 70%);
          animation: cs-float-3 18s ease-in-out infinite;
        }

        @keyframes cs-float-1 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.6; }
          50% { transform: translate(30px, 20px) scale(1.1); opacity: 0.8; }
        }

        @keyframes cs-float-2 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.5; }
          50% { transform: translate(-20px, -30px) scale(1.05); opacity: 0.7; }
        }

        @keyframes cs-float-3 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.4; }
          50% { transform: translate(-15px, 25px) scale(1.15); opacity: 0.6; }
        }

        /* Floating Particles */
        .cs-particles {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 1;
          pointer-events: none;
          overflow: hidden;
        }

        .cs-particle {
          position: absolute;
          bottom: -10px;
          background: linear-gradient(135deg, rgba(168, 85, 247, 0.8), rgba(236, 72, 153, 0.8));
          border-radius: 50%;
          animation: cs-particle-rise linear infinite;
          box-shadow: 0 0 6px rgba(168, 85, 247, 0.5);
        }

        @keyframes cs-particle-rise {
          0% { 
            transform: translateY(0) scale(0);
            opacity: 0;
          }
          10% {
            opacity: 0.8;
            transform: translateY(-10vh) scale(1);
          }
          90% {
            opacity: 0.6;
          }
          100% { 
            transform: translateY(-110vh) scale(0.5);
            opacity: 0;
          }
        }

        /* Glass Card */
        .cs-glass-card {
          background: rgba(255, 255, 255, 0.06);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: 
            0 8px 32px rgba(0, 0, 0, 0.3),
            inset 0 1px 0 rgba(255, 255, 255, 0.1);
        }

        .cs-feature-card {
          background: rgba(255, 255, 255, 0.04);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.08);
          transition: all 0.3s ease;
        }

        .cs-feature-card:hover {
          background: rgba(255, 255, 255, 0.08);
          border-color: rgba(255, 255, 255, 0.15);
          transform: translateY(-4px);
        }

        /* Glow Text */
        .cs-glow-text {
          text-shadow: 0 0 40px rgba(168, 85, 247, 0.5);
        }

        /* Gradient Button */
        .cs-gradient-btn {
          background: linear-gradient(135deg, #8b5cf6 0%, #ec4899 50%, #f97316 100%);
          background-size: 200% 200%;
          animation: cs-gradient-shift 4s ease infinite;
          position: relative;
          overflow: hidden;
        }

        .cs-gradient-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
          animation: cs-btn-shine 3s ease-in-out infinite;
        }

        @keyframes cs-gradient-shift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }

        @keyframes cs-btn-shine {
          0% { left: -100%; }
          50%, 100% { left: 100%; }
        }

        /* Icon Glow */
        .cs-icon-glow {
          filter: drop-shadow(0 0 8px currentColor);
        }

        /* Feature Icon Container */
        .cs-icon-container {
          position: relative;
        }

        .cs-icon-container::before {
          content: '';
          position: absolute;
          inset: -4px;
          background: inherit;
          filter: blur(12px);
          opacity: 0.5;
          border-radius: inherit;
        }

        @media (prefers-reduced-motion: reduce) {
          .cs-aurora-blob,
          .cs-particle,
          .cs-gradient-btn,
          .cs-gradient-btn::before {
            animation: none;
          }
        }
      `}</style>

      {/* Background */}
      <div className="coming-soon-bg">
        <div className="cs-aurora-blob cs-blob-1" />
        <div className="cs-aurora-blob cs-blob-2" />
        <div className="cs-aurora-blob cs-blob-3" />
      </div>

      {/* Particles */}
      <div id="cs-particles" className="cs-particles" />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-20 bg-black/30 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex justify-between items-center">
          <Link href="/" className="flex items-center gap-3 group">
            <div className="w-10 h-10 rounded-xl overflow-hidden shadow-lg shadow-purple-500/30 ring-1 ring-purple-500/30 group-hover:ring-purple-400/50 group-hover:shadow-purple-400/40 transition-all duration-300">
              <Image 
                src="/images/logo-purple.svg" 
                alt="SHOWBOLD" 
                width={40} 
                height={40} 
                className="w-full h-full" 
              />
            </div>
            <span className="text-white font-bold text-xl tracking-tight">SHOWBOLD</span>
          </Link>
          <Link 
            href="/login" 
            className="bg-white/10 hover:bg-white/20 text-white px-5 py-2 rounded-lg text-sm font-medium transition-all border border-white/20"
          >
            Log In
          </Link>
        </div>
      </header>

      {/* Content */}
      <div className="fixed inset-0 z-10 flex min-h-screen flex-col items-center overflow-auto px-5 pt-24 pb-24">
        <div className="relative z-10 w-full max-w-2xl text-center my-auto">
          {/* Main Content Card */}
          <div className="mb-6 md:mb-8 rounded-3xl cs-glass-card p-5 md:p-10">
            {/* Badge */}
            <div className="mb-4 md:mb-6 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-orange-500/20 px-4 py-2 text-sm font-semibold border border-purple-500/30 shadow-lg shadow-purple-500/10">
              <span className="text-base">🚀</span>
              <span className="bg-gradient-to-r from-purple-300 via-pink-300 to-orange-300 bg-clip-text text-transparent">
                Coming Soon
              </span>
            </div>

            <h2 className="mb-3 md:mb-4 text-2xl md:text-4xl font-bold text-white cs-glow-text">
              Something big is coming
            </h2>
            
            <p className="mx-auto mb-6 md:mb-8 max-w-lg text-base md:text-lg leading-relaxed text-gray-300/90">
              Your AI marketing team is almost ready. Join the waitlist for early access.
            </p>

            {status === "success" ? (
              <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-8 backdrop-blur-sm">
                <div className="mb-4 text-5xl">🎉</div>
                <p className="text-xl font-semibold text-emerald-400">
                  {message}
                </p>
                <p className="mt-2 text-gray-400">
                  We&apos;ll notify you the moment we launch.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="mx-auto flex max-w-xl flex-col gap-3 sm:flex-row">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    className="flex-1 h-14 px-5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 focus:bg-white/10 transition-all"
                  />
                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="h-14 px-8 rounded-xl cs-gradient-btn text-white font-bold text-lg shadow-xl shadow-purple-500/30 hover:shadow-purple-500/50 hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {status === "loading" ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Joining...
                      </span>
                    ) : (
                      "Join the Waiting List"
                    )}
                  </button>
                </div>

                {status === "error" && (
                  <p className="text-sm text-red-400">{message}</p>
                )}

                <p className="text-sm text-gray-500">
                  🔒 No spam, ever. Unsubscribe anytime.
                </p>
              </form>
            )}
          </div>

          {/* Features */}
          <div className="mx-auto grid max-w-2xl grid-cols-1 gap-3 sm:grid-cols-3 mb-6 md:mb-8">
            {features.map((feature) => (
              <div 
                key={feature.title}
                className="rounded-2xl cs-feature-card p-4 md:p-5 text-center"
              >
                <div className={`mb-3 mx-auto w-12 h-12 md:w-14 md:h-14 rounded-xl bg-gradient-to-br ${feature.gradient} bg-opacity-20 flex items-center justify-center cs-icon-container`}
                  style={{ background: `linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(236, 72, 153, 0.15))` }}>
                  <div className="relative z-10">
                    {feature.icon}
                  </div>
                </div>
                <p className="mb-1 font-semibold text-white text-sm md:text-base">
                  {feature.title}
                </p>
                <p className="text-xs md:text-sm text-gray-400">
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>

          {/* Back link */}
          <Link
            href="/"
            className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-purple-300 transition-colors group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            Back to Home
          </Link>
        </div>
      </div>
    </>
  );
}
